import { ChartContainer } from '@Components/ChartContainer';
import { HorizontalBarChart } from '@Components/Charts/HorizontalBarChart';
import { Brand30, Brand60 } from '@src/utils/colors';
import {
  CONVERSION_CHART_TITLE,
  CONVERSION_RATE_CHART_TITLE,
  REACH_CHART_TITLE
} from '../audience-penetration-constants';
import { AudiencePenetrationRowWithMetrics, sortByDesc, takeTopN } from '../utils/filter';
import { formatPct } from '../utils/format';

const ROW_HEIGHT = 28;
// HorizontalBarChart paginates internally at 10 rows/page — size the plot area for at most a
// page's worth of bars, or a chart with more rows than that (Top N set to "All") renders a lot of
// blank space below the first page instead of scrolling/growing with what's actually shown.
const MAX_VISIBLE_ROWS = 10;

interface SegmentBarChartProps {
  title: string;
  valueLabel: string;
  rows: AudiencePenetrationRowWithMetrics[];
  metricKey: 'audienceReachPct' | 'audienceConversionPct' | 'conversionRatePct';
  topN: number | null;
  selectedSegmentId?: string;
  onSelectSegment: (segmentId: string) => void;
  isBusy: boolean;
}

const SegmentBarChart = ({
  title,
  valueLabel,
  rows,
  metricKey,
  topN,
  selectedSegmentId,
  onSelectSegment,
  isBusy
}: SegmentBarChartProps) => {
  const chartRows = takeTopN(
    sortByDesc(
      rows.filter(r => r[metricKey] !== null),
      metricKey
    ),
    topN
  );

  return (
    <ChartContainer title={title} isLoading={isBusy}>
      {chartRows.length === 0 ? (
        <div className='audience-penetration-chart-empty'>No data for the current filters.</div>
      ) : (
        <HorizontalBarChart
          data={chartRows}
          barKey={metricKey}
          barFillColor={Brand30}
          yAxisKey='behaviorSegmentName'
          yAxisLabel={valueLabel}
          valueFormatter={formatPct}
          height={Math.min(chartRows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT + 24}
          getRowKey={row => row.behaviorSegmentId}
          selectedRowKey={selectedSegmentId}
          selectedFillColor={Brand60}
          onBarClick={row => onSelectSegment(row.behaviorSegmentId)}
        />
      )}
    </ChartContainer>
  );
};

interface AudiencePenetrationChartsProps {
  rows: AudiencePenetrationRowWithMetrics[];
  topN: number | null;
  selectedSegmentId?: string;
  onSelectSegment: (segmentId: string) => void;
  isBusy: boolean;
}

export const AudiencePenetrationCharts = ({
  rows,
  topN,
  selectedSegmentId,
  onSelectSegment,
  isBusy
}: AudiencePenetrationChartsProps) => (
  <div className='audience-penetration-charts'>
    <SegmentBarChart
      title={REACH_CHART_TITLE}
      valueLabel='Audience Reach %'
      rows={rows}
      metricKey='audienceReachPct'
      topN={topN}
      selectedSegmentId={selectedSegmentId}
      onSelectSegment={onSelectSegment}
      isBusy={isBusy}
    />
    <SegmentBarChart
      title={CONVERSION_CHART_TITLE}
      valueLabel='Audience Conversion %'
      rows={rows}
      metricKey='audienceConversionPct'
      topN={topN}
      selectedSegmentId={selectedSegmentId}
      onSelectSegment={onSelectSegment}
      isBusy={isBusy}
    />
    <SegmentBarChart
      title={CONVERSION_RATE_CHART_TITLE}
      valueLabel='Conversion Rate %'
      rows={rows}
      metricKey='conversionRatePct'
      topN={topN}
      selectedSegmentId={selectedSegmentId}
      onSelectSegment={onSelectSegment}
      isBusy={isBusy}
    />
  </div>
);
